<template>
  <div class="financial-anti-fraud">
    <loading v-if="!$store.state.productLoading"></loading>
    <not-available
      v-else-if="availableProducts.length === 0"
      title="金融反欺诈画像"
    ></not-available>
    <div v-else class="product-content">
      <div class="product-header">
        <h3 v-if="availableProducts.length === 1">{{ selectedName }}</h3>
        <div v-else class="product-select">
          <span @click="toggleProductList">
            {{ selectedName }}
            <i
              class="iconfont iconsanjiaoxingbeifen"
              :class="{ active: showProductList }"
            ></i>
          </span>
          <ul v-if="showProductList" class="product-list">
            <li
              v-for="product in availableProducts"
              :key="product.value"
              :class="{ active: selectedType === product.value }"
              @click="selectProduct(product.value)"
            >
              {{ product.label }}
            </li>
          </ul>
        </div>
      </div>

      <div
        class="authorization-information"
        :class="{ online: isOnline }"
      >
        <div
          class="information-header"
          :style="informationGridStyle"
        >
          <span v-for="label in informationLabels" :key="label">{{ label }}</span>
        </div>
        <div
          v-if="selectedProduct"
          class="information-row"
          :style="informationGridStyle"
        >
          <template v-if="isOnline">
            <span>{{ quotaTotal }}</span>
            <span>{{ quotaRemaining }}</span>
          </template>
          <span>{{ selectedProduct.ctime || '-' }}</span>
          <span>{{ selectedProduct.expire || '-' }}</span>
          <span>使用中</span>
        </div>
      </div>

      <update-package
        v-if="!isOnline"
        :key="selectedType"
        :fetch-data="fetchOfflineLog"
        version=""
        default-upgrade-type="all"
        :upgrade-options="upgradeOptions"
      ></update-package>
    </div>
  </div>
</template>

<script>
import NotAvailable from '../../components/mark/notAvailable'
import UpdatePackage from '../riskPhone/components/updatePackage/index.vue'
import { getFinancialAntiFraudOfflineLog } from '@/services/interface'

const OFFLINE_UPGRADE_OPTIONS = {
  bank_card_all_offline: [
    { label: '全部类型', value: 'all' },
    { label: '初始更新包', value: 'initial' },
    { label: '每日更新包', value: 'day' },
    { label: '小时更新包', value: 'hour' },
    { label: '分钟更新包', value: 'minute' }
  ],
  company_account_offline: [
    { label: '全部类型', value: 'all' },
    { label: '初始更新包', value: 'initial' },
    { label: '每日更新包', value: 'day' },
    { label: '小时更新包', value: 'hour' },
    { label: '分钟更新包', value: 'minute' }
  ],
  digital_rmb_offline: [
    { label: '全部类型', value: 'all' },
    { label: '每日更新包', value: 'day' },
    { label: '小时更新包', value: 'hour' }
  ]
}

export default {
  name: 'FinancialAntiFraud',
  components: { NotAvailable, UpdatePackage },
  data () {
    return {
      selectedType: null,
      showProductList: false,
      products: [
        { label: '银行卡画像', value: 'bank_card_all_offline', online: false },
        { label: '对公账号', value: 'company_account_offline', online: false },
        { label: '洗钱商户', value: 'merchant_online', online: true },
        { label: '受害人涉诈支付', value: 'fraud_victim_payments_online', online: true },
        { label: '数字人民币画像', value: 'digital_rmb_offline', online: false }
      ]
    }
  },
  computed: {
    activeProducts () {
      return this.$store.state.product.filter(product => {
        return product.auditflag === '5'
      })
    },
    availableProducts () {
      return this.products.filter(product => {
        return this.activeProducts.some(activeProduct => {
          return activeProduct.busi === product.value
        })
      })
    },
    selectedDefinition () {
      return this.availableProducts.find(product => {
        return product.value === this.selectedType
      })
    },
    selectedProduct () {
      return this.activeProducts.find(product => {
        return product.busi === this.selectedType
      })
    },
    selectedName () {
      return this.selectedDefinition ? this.selectedDefinition.label : ''
    },
    isOnline () {
      return this.selectedDefinition ? this.selectedDefinition.online : false
    },
    informationLabels () {
      return this.isOnline
        ? ['授权配额', '剩余配额', '开通时间', '截止时间', '状态']
        : ['开通时间', '截止时间', '状态']
    },
    informationGridStyle () {
      return {
        gridTemplateColumns: `repeat(${this.informationLabels.length}, minmax(0, 1fr))`
      }
    },
    quotaTotal () {
      return this.toQuotaNumber(this.selectedProduct && this.selectedProduct.reqtotal)
    },
    quotaRemaining () {
      const used = this.toQuotaNumber(this.selectedProduct && this.selectedProduct.reqused)
      return Math.max(this.quotaTotal - used, 0)
    },
    upgradeOptions () {
      return OFFLINE_UPGRADE_OPTIONS[this.selectedType] || []
    }
  },
  watch: {
    availableProducts: {
      immediate: true,
      handler () {
        this.syncSelectedType(this.$route.params.busi)
      }
    },
    '$route.params.busi' (busi) {
      this.syncSelectedType(busi)
    }
  },
  methods: {
    toQuotaNumber (value) {
      const number = Number(value)
      return Number.isFinite(number) ? number : 0
    },
    syncSelectedType (busi) {
      const routeProduct = this.availableProducts.find(product => {
        return product.value === busi
      })
      if (routeProduct) {
        this.selectedType = routeProduct.value
      } else if (!this.selectedDefinition) {
        this.selectedType = this.availableProducts.length
          ? this.availableProducts[0].value
          : null
      }
    },
    toggleProductList () {
      this.showProductList = !this.showProductList
    },
    selectProduct (busi) {
      this.selectedType = busi
      this.showProductList = false
    },
    fetchOfflineLog (params) {
      return getFinancialAntiFraudOfflineLog({
        ...params,
        product: this.selectedType
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.financial-anti-fraud {
  min-height: 100%;
  padding: 30px 40px 48px;
  box-sizing: border-box;
}

.product-header {
  min-height: 30px;
  margin-bottom: 24px;

  h3,
  .product-select {
    color: #1d243c;
    font-size: 18px;
    font-weight: 500;
    line-height: 30px;
  }
}

.product-select {
  position: relative;
  display: inline-block;

  > span {
    display: inline-flex;
    align-items: center;
    white-space: nowrap;
    cursor: pointer;
  }

  i {
    display: inline-block;
    margin-left: 8px;
    transform: rotate(90deg);

    &.active {
      transform: rotate(270deg);
    }
  }
}

.product-list {
  position: absolute;
  top: 38px;
  left: 0;
  z-index: 99;
  width: max-content;
  min-width: 100%;
  padding: 12px 0;
  background: #fff;
  box-shadow: 0 4px 8px rgba(17, 39, 98, 0.08);

  li {
    height: 40px;
    padding: 0 20px;
    color: #1d243c;
    font-size: 16px;
    font-weight: 400;
    line-height: 40px;
    white-space: nowrap;
    box-sizing: border-box;
    cursor: pointer;

    &:hover,
    &.active {
      background: #dceaff;
    }
  }
}

.authorization-information {
  overflow-x: auto;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 4px 8px rgba(17, 39, 98, 0.04);
}

.information-header,
.information-row {
  display: grid;
  min-width: 480px;

  span {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    padding: 0 8px;
    color: #1d243c;
    text-align: center;
    white-space: nowrap;
    box-sizing: border-box;
  }
}

.authorization-information.online {
  .information-header,
  .information-row {
    min-width: 700px;
  }
}

.information-header {
  border-bottom: 1px solid #eaedf2;

  span {
    height: 58px;
    font-size: 16px;
    font-weight: 500;
  }
}

.information-row span {
  height: 75px;
  font-size: 14px;
  font-weight: 400;
}

@media (max-width: 768px) {
  .financial-anti-fraud {
    padding: 20px 16px 36px;
  }

  .product-header h3,
  .product-header .product-select {
    font-size: 12px;
    line-height: 22px;
  }

  .product-list li {
    padding: 0 12px;
    font-size: 12px;
  }

  .information-header span,
  .information-row span {
    padding: 0 4px;
    font-size: 12px;
  }
}
</style>
